/**
 * submitPayloadHelpers.js
 *
 * Pure helper for assembling the JSON payload sent from the frontend.
 * Keeping this logic outside App.js makes it easy to unit test.
 */

import {
  getScRnaExperimentSampleSheetRule,
  resolveScRnaPlatformLabel,
  sanitizeScRnaSampleSheetRow
} from './scRnaExperimentHelpers';
import { normalizeAnalysisParameters } from './analysisParameterHelpers';
import { applyChIPseqSampleSource } from './chipSeqHelpers';

export const buildSubmitPayload = ({
  data = {},
  normalizeSpeciesFields = value => value,
  getScRNAseqPipelineInfo = () => null,
  getRNAseqPipelineInfo = () => null,
  getChIPseqPipelineInfo = () => null,
  getAnalysisParameterDefinitions = () => [],
  findScRNAExperimentOption = () => null,
  serviceCatalog = {}
} = {}) => {
  const normalized = normalizeSpeciesFields(data) || {};

  const {
    speciesSelection,
    speciesSelectionKey,
    TenXGenomicsRefVersion,
    TenXGenomicsProbeSetVersion,
    TenXGenomicsVDJVersion,
    scRNAExperimentInfo,
    ...submitData
  } = normalized;

  submitData.serviceItems = Array.isArray(submitData.serviceItems) ? submitData.serviceItems : [];

  const scPipelineInfo = getScRNAseqPipelineInfo({
    serviceItems: normalized.serviceItems || [],
    serviceCatalog
  });
  const scRNAExperimentOption = findScRNAExperimentOption(
    scPipelineInfo?.platformLabel,
    scRNAExperimentInfo?.serviceName
  );

  const rnaPipelineInfo = getRNAseqPipelineInfo({
    serviceItems: normalized.serviceItems || [],
    serviceCatalog
  });
  const chipSeqPipelineInfo = getChIPseqPipelineInfo({
    serviceItems: normalized.serviceItems || [],
    serviceCatalog
  });
  const selectedVisiumHDService = (Array.isArray(normalized.serviceItems) ? normalized.serviceItems : [])
    .flatMap(item => (Array.isArray(item?.services) ? item.services : []))
    .find(svc => {
      const serviceName = svc?.service;
      if (!serviceName) return false;

      const serviceConfig = Object.values(serviceCatalog || {})
        .flatMap(category => (Array.isArray(category) ? category : []))
        .find(item => item?.value === serviceName);
      return serviceConfig?.isVisiumHD === true || String(serviceName).includes('VisiumHD-10X');
    });

  const visiumHDPipelineLabel = (() => {
    const serviceName = String(selectedVisiumHDService?.service || '');
    if (!serviceName) return '';
    return /customized/i.test(serviceName) ? 'Customized' : 'Basic';
  })();

  if (submitData.sampleInfo && typeof submitData.sampleInfo === 'object') {
    submitData.sampleInfo = { ...submitData.sampleInfo };
    delete submitData.sampleInfo.pipeline;

    // Sample Sheet 的額外欄位（Sample_Barcode / Fastq_Name / V(D)J 配對）只屬於特定實驗類型。
    // 以「這張單目前真的跑得到的實驗類型」為準，換實驗類型或換品項後舊值不會跟著送出。
    // findScRNAExperimentOption 找不到對應選項時代表它已不適用，規則會退回空規則。
    const sampleSheetRule = getScRnaExperimentSampleSheetRule(scRNAExperimentOption?.ServiceName || '');

    // 「樣本來源」只有 ChIP-seq 單才有；換到其他品項後整個 key 拿掉。
    const isChIPseqOrder = Boolean(chipSeqPipelineInfo?.pipelineLabel);

    if (Array.isArray(submitData.sampleInfo.sampleSheet)) {
      submitData.sampleInfo.sampleSheet = submitData.sampleInfo.sampleSheet
        .map(row => applyChIPseqSampleSource(sanitizeScRnaSampleSheetRow(row, sampleSheetRule), isChIPseqOrder));
    }
  }

  // 「分析參數」只送目前品項真的要填的那幾個，值也要是選單裡的合法值、數字轉成 number；
  // 換品項後不再適用的參數不會殘留（例如 Advanced 換成 Basic 就沒有 log2FoldChange / pAdjust）。
  // 這張單沒有任何分析參數時整個欄位拿掉。
  const analysisParameterDefinitions = getAnalysisParameterDefinitions({
    serviceItems: normalized.serviceItems || [],
    serviceCatalog
  });

  if (analysisParameterDefinitions.length > 0) {
    submitData.analysisParameters = normalizeAnalysisParameters(
      analysisParameterDefinitions,
      normalized.analysisParameters
    );
  } else if (Object.prototype.hasOwnProperty.call(submitData, 'analysisParameters')) {
    delete submitData.analysisParameters;
  }

  if (scPipelineInfo?.pipelineLabel) {
    submitData.TenXGenomicsRefVersion = TenXGenomicsRefVersion || '';
    submitData.TenXGenomicsProbeSetVersion = TenXGenomicsProbeSetVersion || '';
    submitData.TenXGenomicsVDJVersion = TenXGenomicsVDJVersion || '';
    submitData.serviceName = scRNAExperimentOption?.ServiceName || scRNAExperimentInfo?.serviceName || '';
    submitData.pipeline = scPipelineInfo.pipelineLabel;
    submitData.platform_type = resolveScRnaPlatformLabel(scRNAExperimentOption, scPipelineInfo.platformLabel);
    submitData.chemistry_type = scRNAExperimentOption?.chemistry_type || '';
    submitData.multi_type = scRNAExperimentOption?.multi_type || '';
  } else if (rnaPipelineInfo?.pipelineLabel) {
    submitData.pipeline = rnaPipelineInfo.pipelineLabel;
    if (Object.prototype.hasOwnProperty.call(submitData, 'platform_type')) delete submitData.platform_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'serviceName')) delete submitData.serviceName;
    if (Object.prototype.hasOwnProperty.call(submitData, 'chemistry_type')) delete submitData.chemistry_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'multi_type')) delete submitData.multi_type;
  } else if (chipSeqPipelineInfo?.pipelineLabel) {
    // ChIP-seq 和 bulk RNA-seq 一樣不需要平台 / 實驗類型欄位。
    submitData.pipeline = chipSeqPipelineInfo.pipelineLabel;
    if (Object.prototype.hasOwnProperty.call(submitData, 'platform_type')) delete submitData.platform_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'serviceName')) delete submitData.serviceName;
    if (Object.prototype.hasOwnProperty.call(submitData, 'chemistry_type')) delete submitData.chemistry_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'multi_type')) delete submitData.multi_type;
  } else if (visiumHDPipelineLabel) {
    submitData.TenXGenomicsRefVersion = TenXGenomicsRefVersion || '';
    submitData.TenXGenomicsProbeSetVersion = TenXGenomicsProbeSetVersion || '';
    submitData.TenXGenomicsVDJVersion = '';
    submitData.pipeline = visiumHDPipelineLabel;
    submitData.platform_type = '10X Genomics';
    if (Object.prototype.hasOwnProperty.call(submitData, 'serviceName')) delete submitData.serviceName;
    if (Object.prototype.hasOwnProperty.call(submitData, 'chemistry_type')) delete submitData.chemistry_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'multi_type')) delete submitData.multi_type;
  } else {
    if (Object.prototype.hasOwnProperty.call(submitData, 'pipeline')) delete submitData.pipeline;
    if (Object.prototype.hasOwnProperty.call(submitData, 'platform_type')) delete submitData.platform_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'serviceName')) delete submitData.serviceName;
    if (Object.prototype.hasOwnProperty.call(submitData, 'chemistry_type')) delete submitData.chemistry_type;
    if (Object.prototype.hasOwnProperty.call(submitData, 'multi_type')) delete submitData.multi_type;
  }

  return submitData;
};
